/**
 * Workflow Action Atoms - Write operations for workflows
 *
 * These atoms handle all workflow mutations (create, update, delete).
 * They update local state optimistically and mark entities as dirty for auto-save.
 * Uses validated fetch with Zod schemas for runtime type safety.
 */

import { atom } from "jotai";
import {
  _workflowsAtom,
  _workflowsInDBAtom,
  _workspaceWorkflowsAtom,
  currentWorkflowIdAtom,
  workspaceWorkflowsStatusAtom,
  currentWorkflowStatusAtom,
  workspaceWorkflowsErrorAtom,
  currentWorkflowErrorAtom,
} from "../core/workflows";
import { _phasesAtom, _phasesInDBAtom } from "../core/phases";
import { sessionAtom } from "../core/user";
import { API_ROUTES } from "@/frontend/lib/api/routes";
import {
  WorkflowSchema,
  WorkflowListItemSchema,
  PhaseSchema,
  type Workflow,
  type CreateWorkflowApiPayload,
  type UpdateWorkflowApiPayload,
} from "@/isomorphic/schemas";
import {
  fetchValidatedArray,
  fetchValidated,
  postValidated,
  putValidated,
  deleteValidated,
} from "@/frontend/lib/validation";
import { addToHistoryAtom } from "./history-actions";
// ============================================================================
// INITIALIZATION
// ============================================================================

/**
 * Initialize workflow - Load workflow and its phases by workflowId
 */
export const initWorkflowAtom = atom(null, async (get, set, workflowId: string) => {
  const session = get(sessionAtom);
  if (!session) {
    console.error("[WorkflowActions] No session available");
    return;
  }

  const jwt = session.access_token;

  set(currentWorkflowStatusAtom, "loading");
  set(currentWorkflowErrorAtom, null);

  // Load workflow and phases in parallel with validation
  const [workflowResult, phasesResult] = await Promise.all([
    fetchValidated(WorkflowSchema, API_ROUTES.workflows.getById(workflowId), {
      jwt,
      context: `Workflow ${workflowId}`,
    }),
    fetchValidatedArray(PhaseSchema, API_ROUTES.phases.getByWorkflow(workflowId), {
      jwt,
      context: `Workflow ${workflowId} Phases`,
    }),
  ]);

  if (workflowResult.success && phasesResult.success) {
    // Load data directly (loading data, not user edit - no history)
    set(currentWorkflowIdAtom, workflowId);
    set(_workflowsAtom, [workflowResult.data]);
    set(_phasesAtom, phasesResult.data);

    // Update DB state references
    set(_workflowsInDBAtom, [workflowResult.data]);
    set(_phasesInDBAtom, phasesResult.data);

    set(currentWorkflowStatusAtom, "success");
    console.log("[WorkflowActions] Workflow initialized:", workflowId);
  } else {
    const error = !workflowResult.success
      ? workflowResult.error
      : !phasesResult.success
        ? phasesResult.error
        : "Unknown error";
    console.error("[WorkflowActions] Error initializing workflow:", error);
    set(currentWorkflowStatusAtom, "error");
    set(currentWorkflowErrorAtom, error);
  }
});

/**
 * Initialize workflow by agent ID - Load workflow and its phases from agent
 * This is the preferred method when navigating from agent list
 */
export const initWorkflowByAgentAtom = atom(null, async (get, set, params: { agentId: string; jwt: string }) => {
  const { agentId, jwt } = params;

  set(currentWorkflowStatusAtom, "loading");
  set(currentWorkflowErrorAtom, null);

  try {
    // Fetch workflow with phases in a single call
    const response = await fetch(API_ROUTES.workflows.getByAgentWithPhases(agentId), {
      headers: {
        Authorization: `Bearer ${jwt}`,
        "Content-Type": "application/json",
      },
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.error || `HTTP ${response.status}`);
    }

    const responseData = await response.json();
    const data = responseData.data; // API wraps response in { data: ... }

    // Validate workflow
    const workflowResult = WorkflowSchema.safeParse({
      id: data.id,
      agentId: data.agentId,
      workflowGroupId: data.workflowGroupId,
      releaseVersion: data.releaseVersion,
      historyVersion: data.historyVersion,
      status: data.status,
      createdAt: data.createdAt,
      updatedAt: data.updatedAt,
      name: data.name,
      description: data.description,
      creatorId: data.creatorId, // Now required from API
    });

    if (!workflowResult.success) {
      console.error("[WorkflowActions] Invalid workflow data:", workflowResult.error);
      throw new Error("Invalid workflow data format");
    }

    // Validate phases
    const phases = data.phases || [];
    const validatedPhases = phases
      .map((phase: unknown) => {
        const result = PhaseSchema.safeParse(phase);
        if (!result.success) {
          console.warn("[WorkflowActions] Invalid phase data:", result.error);
          return null;
        }
        return result.data;
      })
      .filter(Boolean);

    // Load data directly (loading data, not user edit - no history)
    set(currentWorkflowIdAtom, workflowResult.data.id);
    set(_workflowsAtom, [workflowResult.data]);
    set(_phasesAtom, validatedPhases);

    // Update DB state references
    set(_workflowsInDBAtom, [workflowResult.data]);
    set(_phasesInDBAtom, validatedPhases);

    set(currentWorkflowStatusAtom, "success");
    console.log("[WorkflowActions] Workflow initialized by agent:", agentId, "workflowId:", workflowResult.data.id);
  } catch (error) {
    const errorMessage = error instanceof Error ? error.message : "Unknown error";
    console.error("[WorkflowActions] Error initializing workflow by agent:", errorMessage);
    set(currentWorkflowStatusAtom, "error");
    set(currentWorkflowErrorAtom, errorMessage);
  }
});

/**
 * Load workspace workflows
 */
export const loadWorkspaceWorkflowsAtom = atom(null, async (get, set, workspaceId: string) => {
  const session = get(sessionAtom);
  if (!session) {
    console.error("[WorkflowActions] No session available");
    return;
  }

  const jwt = session.access_token;

  set(workspaceWorkflowsStatusAtom, "loading");
  set(workspaceWorkflowsErrorAtom, null);

  const result = await fetchValidatedArray(WorkflowListItemSchema, API_ROUTES.workflows.getByWorkspace(workspaceId), {
    jwt,
    context: "Workspace Workflows",
  });

  if (result.success) {
    set(_workspaceWorkflowsAtom, result.data);
    set(workspaceWorkflowsStatusAtom, "success");
    console.log("[WorkflowActions] Workspace workflows loaded:", result.data.length);
  } else {
    console.error("[WorkflowActions] Error loading workspace workflows:", result.error);
    set(workspaceWorkflowsStatusAtom, "error");
    set(workspaceWorkflowsErrorAtom, result.error);
  }
});

// ============================================================================
// MUTATIONS
// ============================================================================

/**
 * Update workflow name
 */
export const updateWorkflowNameAtom = atom(
  null,
  async (get, set, { workflowId, name }: { workflowId: string; name: string }) => {
    // Optimistic update
    const workflows = get(_workflowsAtom);
    set(
      _workflowsAtom,
      workflows.map((w) => (w.id === workflowId ? { ...w, name } : w)),
    );

    // Add to history
    set(addToHistoryAtom);

    console.log("[WorkflowActions] Workflow name updated (optimistic):", workflowId, name);
  },
);

/**
 * Update workflow description
 */
export const updateWorkflowDescriptionAtom = atom(
  null,
  async (get, set, { workflowId, description }: { workflowId: string; description: string }) => {
    // Optimistic update
    const workflows = get(_workflowsAtom);
    set(
      _workflowsAtom,
      workflows.map((w) => (w.id === workflowId ? { ...w, description } : w)),
    );

    // Add to history
    set(addToHistoryAtom);

    console.log("[WorkflowActions] Workflow description updated (optimistic):", workflowId);
  },
);

/**
 * Update workflow (generic) - For complex updates or immediate save
 *
 * Use this when:
 * - You need to update multiple fields at once
 * - You need immediate save (bypass auto-save)
 * - You're updating fields other than name/description
 *
 * For simple name/description updates, prefer specific atoms above.
 */
export const updateWorkflowAtom = atom(
  null,
  async (
    get,
    set,
    {
      workflowId,
      payload,
      immediate = false,
    }: {
      workflowId: string;
      payload: UpdateWorkflowApiPayload;
      immediate?: boolean;
    },
  ): Promise<Workflow | null> => {
    const session = get(sessionAtom);
    if (!session) {
      console.error("[WorkflowActions] No session available");
      return null;
    }

    const jwt = session.access_token;

    // Optimistic update
    const workflows = get(_workflowsAtom);
    set(
      _workflowsAtom,
      workflows.map((w) => (w.id === workflowId ? { ...w, ...payload } : w)),
    );

    // Add to history
    set(addToHistoryAtom);

    if (immediate) {
      // Immediate save (bypass auto-save) with validation
      const result = await putValidated(WorkflowSchema, API_ROUTES.workflows.update(workflowId), payload, {
        jwt,
        context: `Update Workflow ${workflowId}`,
      });

      if (result.success) {
        // Update with validated server response
        set(
          _workflowsAtom,
          workflows.map((w) => (w.id === workflowId ? result.data : w)),
        );
        console.log("[WorkflowActions] Workflow updated (immediate):", workflowId);
        return result.data;
      } else {
        console.error("[WorkflowActions] Error updating workflow:", result.error);
        // Rollback optimistic update
        set(_workflowsAtom, workflows);
        throw new Error(result.error);
      }
    } else {
      // Dirty + history automatic
      console.log("[WorkflowActions] Workflow updated (optimistic):", workflowId);
      return null;
    }
  },
);

/**
 * Create workflow
 */
export const createWorkflowAtom = atom(
  null,
  async (get, set, payload: CreateWorkflowApiPayload): Promise<Workflow | null> => {
    const session = get(sessionAtom);
    if (!session) {
      console.error("[WorkflowActions] No session available");
      return null;
    }

    const jwt = session.access_token;

    const result = await postValidated(WorkflowSchema, API_ROUTES.workflows.create, payload, {
      jwt,
      context: "Create Workflow",
    });

    if (result.success) {
      // Update local state
      const workflows = get(_workflowsAtom);
      set(_workflowsAtom, [...workflows, result.data]);

      // Add to history
      set(addToHistoryAtom);

      console.log("[WorkflowActions] Workflow created:", result.data.id);
      return result.data;
    } else {
      console.error("[WorkflowActions] Error creating workflow:", result.error);
      throw new Error(result.error);
    }
  },
);

/**
 * Delete workflow
 */
export const deleteWorkflowAtom = atom(null, async (get, set, workflowId: string): Promise<void> => {
  const session = get(sessionAtom);
  if (!session) {
    console.error("[WorkflowActions] No session available");
    return;
  }

  const jwt = session.access_token;

  const result = await deleteValidated(API_ROUTES.workflows.delete(workflowId), {
    jwt,
    context: `Delete Workflow ${workflowId}`,
  });

  if (result.success) {
    // Update local state
    const workflows = get(_workflowsAtom);
    set(
      _workflowsAtom,
      workflows.filter((w) => w.id !== workflowId),
    );

    // Also remove from workspace workflows
    const workspaceWorkflows = get(_workspaceWorkflowsAtom);
    set(
      _workspaceWorkflowsAtom,
      workspaceWorkflows.filter((w) => w.id !== workflowId),
    );

    // Add to history
    set(addToHistoryAtom);

    console.log("[WorkflowActions] Workflow deleted:", workflowId);
  } else {
    console.error("[WorkflowActions] Error deleting workflow:", result.error);
    throw new Error(result.error);
  }
});
